import type React from "react";
import { useCallback, useEffect, useRef } from "react";

import { Tree } from "components/objectTree/types";
import { ModelTreeNodeData } from "features/modelTree/types";
import { NodeType } from "types/misc";
import { AppObjectDB } from "utils/appObjectDb";
import { getDescendants } from "utils/search";

/**
 * Maximum concurrent preload requests to avoid overwhelming the network
 * when expanding a folder with many children.
 */
const MAX_PRELOAD_CONCURRENCY = 5;

/**
 * Returns a callback that, when called with a node path after expansion,
 * preloads `descendants` for the newly visible child folder nodes in the
 * background. This warms the cache so that subsequent checkbox clicks on
 * those children resolve instantly without network calls.
 *
 * Preloading is best-effort: failures are silently ignored and do not
 * affect the user experience. Children that can't be processed immediately
 * due to the concurrency limit are queued and processed as slots free up.
 */
export function usePreloadOnExpand(
    db: AppObjectDB | undefined,
    treeRef: React.RefObject<Tree<ModelTreeNodeData>>,
): (expandedPath: string) => void {
    const inflightRef = useRef(new Set<string>());
    const queueRef = useRef(new Set<string>());
    const abortControllerRef = useRef<AbortController | null>(null);

    // Create a fresh AbortController per db instance and cancel any in-flight
    // preloads on unmount or when the db changes (e.g. scene switch), so stale
    // requests don't keep running or mutate descendants on outdated nodes.
    useEffect(() => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const inflight = inflightRef.current;
        const queue = queueRef.current;

        return () => {
            abortController.abort();
            abortControllerRef.current = null;
            inflight.clear();
            queue.clear();
        };
    }, [db]);

    return useCallback(
        (expandedPath: string) => {
            if (!db) {
                return;
            }

            const signal = abortControllerRef.current?.signal;
            if (!signal || signal.aborted) {
                return;
            }

            const tree = treeRef.current;
            if (!tree) {
                return;
            }

            const expandedNode = tree[expandedPath];
            if (!expandedNode?.children) {
                return;
            }

            // Collect eligible children to preload
            for (const childPath of expandedNode.children) {
                if (inflightRef.current.has(childPath) || queueRef.current.has(childPath)) {
                    continue;
                }

                const child = tree[childPath];
                if (!child || child.isLeaf || child.data.object.type === NodeType.Leaf) {
                    continue;
                }

                if (child.data.object.descendants !== undefined) {
                    continue;
                }

                queueRef.current.add(childPath);
            }

            // Drain the queue up to the concurrency limit
            drainQueue(db, treeRef, inflightRef, queueRef, signal);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- treeRef is a stable ref object; only db affects behavior
        [db],
    );
}

function drainQueue(
    db: AppObjectDB,
    treeRef: React.RefObject<Tree<ModelTreeNodeData>>,
    inflightRef: React.MutableRefObject<Set<string>>,
    queueRef: React.MutableRefObject<Set<string>>,
    signal: AbortSignal,
): void {
    while (!signal.aborted && queueRef.current.size > 0 && inflightRef.current.size < MAX_PRELOAD_CONCURRENCY) {
        // Set iterates in insertion order, so this is FIFO
        const childPath = queueRef.current.values().next().value!;
        queueRef.current.delete(childPath);

        // Re-check tree state in case it changed since queuing
        const tree = treeRef.current;
        if (!tree) {
            break;
        }

        const child = tree[childPath];
        if (!child || child.data.object.descendants !== undefined) {
            continue;
        }

        inflightRef.current.add(childPath);

        getDescendants({ db, parentNode: child.data.object, abortSignal: signal })
            .catch(() => {
                // Best-effort — silently ignore failures (including AbortError)
            })
            .finally(() => {
                inflightRef.current.delete(childPath);
                // Process next queued item when a slot frees up
                drainQueue(db, treeRef, inflightRef, queueRef, signal);
            });
    }
}
